import { useState, useCallback } from 'react';
import SectionTitle from '@/components/base/SectionTitle';

const tarifs = [
  { label: 'Face A', prix: 'CHF 300.–' },
  { label: 'Face B', prix: 'CHF 400.–' },
  { label: 'Bandes', prix: 'CHF 100.–' },
  { label: 'Pack complet', prix: 'CHF 700.-', highlight: true, tagline: 'Pour toutes les faces et bandes' },
];

const slides = [
  {
    src: 'https://storage.readdy-site.link/project_files/d8db5950-dd7c-40d9-b2de-72ddcdf21388/40aac151-ac47-4dc3-b07f-d4a6383157cb_personnalisation_hd.png?v=9b84f374cedfe7a50a4ce634da19921c',
    alt: 'Vue de la remorque avec les zones de personnalisation',
  },
  {
    src: 'https://storage.helloreaddy.io/project_files/d8db5950-dd7c-40d9-b2de-72ddcdf21388/e0093e04-b500-4ebf-ab05-02d02949e5f1_compressed_remorque_face_perso.webp',
    alt: 'Remorque personnalisée Beach Food Festival – vue de face',
  },
  {
    src: 'https://storage.helloreaddy.io/project_files/d8db5950-dd7c-40d9-b2de-72ddcdf21388/6d240cfc-00b3-4718-aae8-7e1fb48332a1_compressed_remorque_face_perso2.webp',
    alt: 'Remorque personnalisée Beach Food Festival – vue de côté',
  },
];

export default function Personalisation() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  const goNext = useCallback(() => {
    setCurrentIndex((prev) => Math.min(prev + 1, slides.length - 1));
  }, []);

  const goPrev = useCallback(() => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  }, []);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStart - touchEnd > 50) {
      goNext();
    }
    if (touchStart - touchEnd < -50) {
      goPrev();
    }
  };

  return (
    <section id="personnalisation" className="py-20 sm:py-28 lg:py-16 bg-[#fbf9f4]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionTitle
          label="Branding"
          title="Personnalisation"
          delay={100}
        />

        <p className="text-center text-gray-700 text-base md:text-lg max-w-3xl mx-auto mb-12 md:mb-16 lg:mb-14 leading-relaxed">
          Une face avant personnalisable pour s’intégrer parfaitement à l’univers de votre événement.
        </p>

        {/* Carrousel d'images */}
        <div className="relative w-full max-w-4xl mx-auto mb-12 md:mb-16 lg:mb-14 animate-fade-in-up delay-200">
          <div
            className="relative rounded-xl overflow-hidden"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div
              className="flex transition-transform duration-500 ease-out"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {slides.map((slide) => (
                <div key={slide.src} className="w-full flex-shrink-0">
                  <img
                    src={slide.src}
                    alt={slide.alt}
                    className="w-full h-auto object-contain"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Flèche gauche */}
          <button
            onClick={goPrev}
            disabled={currentIndex === 0}
            className="absolute left-2 md:left-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 md:w-10 md:h-10 rounded-full bg-white/90 backdrop-blur-sm border border-gray-200/60 shadow-md flex items-center justify-center text-[#00A8B5] hover:bg-[#00A8B5] hover:text-white hover:border-[#00A8B5] transition-all disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
            aria-label="Image précédente"
          >
            <i className="ri-arrow-left-s-line text-lg md:text-xl"></i>
          </button>

          {/* Flèche droite */}
          <button
            onClick={goNext}
            disabled={currentIndex === slides.length - 1}
            className="absolute right-2 md:right-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 md:w-10 md:h-10 rounded-full bg-white/90 backdrop-blur-sm border border-gray-200/60 shadow-md flex items-center justify-center text-[#00A8B5] hover:bg-[#00A8B5] hover:text-white hover:border-[#00A8B5] transition-all disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
            aria-label="Image suivante"
          >
            <i className="ri-arrow-right-s-line text-lg md:text-xl"></i>
          </button>

          {/* Indicateurs */}
          <div className="flex justify-center items-center gap-2 mt-5">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentIndex(i)}
                className={`rounded-full transition-all cursor-pointer ${
                  i === currentIndex
                    ? 'bg-[#00A8B5] w-6 h-2'
                    : 'bg-[#00A8B5]/30 w-2 h-2 hover:bg-[#00A8B5]/50'
                }`}
                aria-label={`Aller à l'image ${i + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Bloc tarifaire */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 max-w-4xl mx-auto animate-fade-in-up delay-300">
          {tarifs.map((t) => (
            <div
              key={t.label}
              className={`rounded-lg p-5 md:p-6 text-center transition-all duration-[250ms] ease-out hover:-translate-y-1 hover:shadow-lg hover:scale-[1.01] cursor-default ${
                t.highlight
                  ? 'bg-[#00A8B5] text-white border border-[#00A8B5]'
                  : 'bg-white border border-[#00A8B5]/20 hover:border-[#00A8B5]/50'
              }`}
            >
              <p
                className={`text-sm font-semibold mb-1 ${
                  t.highlight ? 'text-white/90' : 'text-[#00A8B5]'
                }`}
              >
                {t.label}
              </p>
              {t.tagline && (
                <p className="text-xs text-white/80 mb-2">{t.tagline}</p>
              )}
              <p
                className={`text-xl md:text-2xl font-extrabold ${
                  t.highlight ? 'text-white' : 'text-gray-900'
                }`}
              >
                {t.prix}
              </p>
            </div>
          ))}
        </div>

        {/* CTA Personnalisation */}
        <div className="mt-12 md:mt-16 lg:mt-14 text-center animate-fade-in-up delay-400">
          <p className="text-sm text-gray-500 mb-4 max-w-md mx-auto leading-relaxed">
            Une façade brandable adaptée à votre univers
          </p>
          <a
            href="#devis"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById('devis')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="inline-block w-full sm:w-auto bg-[#00A8B5] hover:bg-[#008f9b] text-black font-semibold py-4 px-8 rounded-md transition-all duration-300 whitespace-nowrap cursor-pointer text-center"
          >
            Je personnalise ma remorque
          </a>
        </div>
      </div>
    </section>
  );
}